import { staticDefaults } from '@/config/static/catalog';
import type { DeviceDraft } from '@/types/deviceSetup';
import { normalizeMentionText } from '@/utils/atMention';

const DEVICE_INTENT =
  /(?:add|create|setup|install|set\s+up)\s+(?:(?:a\s+)?(?:new\s+)?)?/i;

const DEVICE_NOUN = /controller|device|panel|lp\d+|ep\d+/i;

const IPV4 = /\b(\d{1,3}(?:\.\d{1,3}){3})\b/;

function resolveManufacturer(raw: string): string | undefined {
  const lower = raw.toLowerCase();
  for (const name of Object.keys(staticDefaults.device.manufacturers)) {
    if (lower.includes(name.toLowerCase()) || lower.includes('mercury') && name.toLowerCase().includes('mercury')) {
      return name;
    }
  }
  if (/mercury/i.test(raw)) return 'Mercury security';
  return undefined;
}

function resolveModel(text: string, manufacturer?: string): string | undefined {
  const models = manufacturer ? staticDefaults.device.manufacturers[manufacturer] : Object.values(staticDefaults.device.manufacturers).flat();
  const upper = text.toUpperCase();
  for (const model of models) {
    if (upper.includes(model.toUpperCase())) return model;
  }
  const generic = text.match(/\b(LP\d+|EP\d+|A\d+)\b/i);
  return generic ? generic[1].toUpperCase() : undefined;
}

export function isDeviceSetupMessage(raw: string): boolean {
  const text = normalizeMentionText(raw.trim());
  return DEVICE_INTENT.test(text) && DEVICE_NOUN.test(text);
}

export function parseDeviceSetupPrefillFromMessage(raw: string): Partial<DeviceDraft> | null {
  const text = normalizeMentionText(raw.trim());
  if (!isDeviceSetupMessage(text)) return null;

  const seed: Partial<DeviceDraft> = { category: 'door-controller' };

  const manufacturer = resolveManufacturer(text);
  if (manufacturer) seed.manufacturer = manufacturer;

  const model = resolveModel(text, manufacturer);
  if (model) seed.model = model;

  const nameMatch = text.match(/\b(?:named|name(?:d)?|with\s+name)\s+([^,]+?)(?=\s+by\b|\s+at\b|,|$)/i);
  if (nameMatch) seed.name = nameMatch[1].trim();

  const ipMatch = text.match(/(?:ip\s+address|ip|at)\s+(\d{1,3}(?:\.\d{1,3}){3})/i) ?? text.match(IPV4);
  if (ipMatch) seed.ipAddress = ipMatch[1] ?? ipMatch[0];

  const portMatch = text.match(/\bport\s+(\d+)\b/i);
  if (portMatch) seed.port = portMatch[1];

  const userMatch = text.match(/\busername\s+([^\s,.]+)/i);
  if (userMatch) seed.username = userMatch[1];

  if (/tcp\s*\/?\s*ip/i.test(text)) seed.connectionType = 'TCP/IP';

  if (
    !seed.manufacturer &&
    !seed.model &&
    !seed.name &&
    !seed.ipAddress &&
    !seed.port &&
    !seed.username
  ) {
    return seed.category ? { category: seed.category } : null;
  }

  return seed;
}

export function hasDeviceSetupPrefill(raw: string): boolean {
  const prefill = parseDeviceSetupPrefillFromMessage(raw);
  if (!prefill) return false;
  return Boolean(
    prefill.manufacturer ||
      prefill.model ||
      prefill.name ||
      prefill.ipAddress ||
      prefill.port ||
      prefill.username
  );
}

/** @deprecated Use parseDeviceSetupPrefillFromMessage */
export const parseDeviceSetupSeedFromMessage = parseDeviceSetupPrefillFromMessage;

/** @deprecated Use hasDeviceSetupPrefill */
export const hasDeviceSetupSeed = hasDeviceSetupPrefill;
